import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import { BackgroundStories, Chart } from '../';
import { Sembilan, Johor, Melaka } from '../../map';
import {
  deriveChartData,
  deriveFillsFromSeats,
} from '../SeatDetail/seatHelpers';

const mapByKey = {
  negerisembilan: Sembilan,
  johor: Johor,
  melaka: Melaka,
};

const StateCard = ({ state, unofficial, seats, pastSeats }) => {
  const [tab, setTab] = useState('current');
  const isLast = tab === 'last';
  const MapComp = mapByKey[state.mapKey];

  // Map fills + chart bars derived from Home's seatinfo data. When a dataset
  // is missing (state not fetched by Home, or past still loading) the value is
  // undefined and the child falls back to its own chart/getInfo fetch.
  const fills = useMemo(
    () => (seats ? deriveFillsFromSeats(seats) : undefined),
    [seats]
  );
  const lastFills = useMemo(
    () => (pastSeats ? deriveFillsFromSeats(pastSeats) : undefined),
    [pastSeats]
  );
  const chartData = useMemo(
    () => (seats ? deriveChartData(seats) : undefined),
    [seats]
  );
  const lastChartData = useMemo(
    () => (pastSeats ? deriveChartData(pastSeats) : undefined),
    [pastSeats]
  );
  return (
    <article className="state-card">
      <header className="state-card__head">
        <div>
          <h3 className="state-card__name">{i18next.t(state.nameKey)}</h3>
          <span className="state-card__seats">{state.totalSeats} 席</span>
        </div>
        {!isLast && (
          <span className="live-pulse">
            <span className="live-pulse__dot" /> LIVE
          </span>
        )}
      </header>
      <div className="state-card__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`state-card__tab ${!isLast ? 'is-active' : ''}`}
          aria-selected={!isLast}
          onClick={() => setTab('current')}
        >
          {i18next.t('本届')}
        </button>
        <button
          type="button"
          role="tab"
          className={`state-card__tab ${isLast ? 'is-active' : ''}`}
          aria-selected={isLast}
          onClick={() => setTab('last')}
        >
          {i18next.t('上届')}
        </button>
      </div>
      {unofficial && !isLast && (
        <div className="state-card__unofficial">（非官方成绩）</div>
      )}
      <Link
        to={`/seats/${state.slug}/n`}
        className="state-card__panels"
        aria-label={`${i18next.t(state.nameKey)} ${i18next.t('议席列表')}`}
      >
        {MapComp && (
          <div className="state-card__map" aria-hidden="true">
            <MapComp
              state={state.slug}
              code="n"
              lastElection={isLast}
              fills={isLast ? lastFills : fills}
              static
            />
          </div>
        )}
        <div className="state-card__chart">
          <Chart
            stateSlug={state.slug}
            totalSeats={state.totalSeats}
            height={260}
            lastElection={isLast}
            data={isLast ? lastChartData : chartData}
          />
        </div>
      </Link>
      <BackgroundStories state={state} />
      <Link
        to={`/seats/${state.slug}/n`}
        className="state-card__cta"
        aria-label={`${i18next.t(state.nameKey)} ${i18next.t('议席列表')}`}
      >
        查看议席 →
      </Link>
    </article>
  );
};

const DashboardGrid = ({ states, unofficial, seatsBySlug = {}, pastBySlug = {} }) => {
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onScroll = () => {
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const cardCenter = el.offsetLeft + el.offsetWidth / 2;
        const dist = Math.abs(cardCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setActiveIndex(closestIdx);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = cardRefs.current[i];
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;
    const left = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
    scroller.scrollTo({ left, behavior: 'smooth' });
  };

  return (
    <section className="dashboard-section" aria-label="State results">
      {states.length > 1 && (
        <div
          className="dashboard-section__tabs"
          role="tablist"
          aria-label="States"
        >
          {states.map((s, i) => (
            <button
              key={s.slug}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              className={`dashboard-tab ${i === activeIndex ? 'is-active' : ''}`}
              onClick={() => scrollTo(i)}
            >
              {i18next.t(s.nameKey)}
            </button>
          ))}
        </div>
      )}
      <div className="dashboard-grid" ref={scrollerRef}>
        {states.map((s, i) => (
          <div
            key={s.slug}
            ref={(el) => { cardRefs.current[i] = el; }}
            className="dashboard-grid__item"
          >
            <StateCard
              state={s}
              unofficial={unofficial}
              seats={seatsBySlug[s.slug] || undefined}
              pastSeats={pastBySlug[s.slug] || undefined}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default DashboardGrid;
